import type { AssetDataBynder, AssetDataKontentAi, BikefinderAnswer, BikefinderQuestion, Dialog, NewsletterWidgetData } from '~/@types/cms';
import type { BikefinderQuery_Collection_Products_Items } from '~/@types/nestedGraphqlTypes';
import type { BikefinderQuery, ProductPreview } from '~/graphql/generated';

export type BikeAppStore = Partial<ProductPreview> & { range: string };
type BikesByRangeType = {
    code: string;
    sort: number;
    bikes: string[];
};
type Bikefinder = {
    how_it_works: LinkData;
    link: LinkData;
    disable_theme?: 'disable';
    age: {
        question: string;
        description?: string;
        dialog: WidgetContent<Dialog>;
    };
    height: {
        question: string;
        description?: string;
        dialog: WidgetContent<Dialog>;
    };
    flow: [null, BikefinderQuestion, BikefinderQuestion, BikefinderQuestion, BikefinderQuestion, BikefinderQuestion, BikefinderQuestion];
    result: {
        title: string;
        steps_breadcrumb_heading: string;
        recommendation_heading: string;
        recommendations_heading: string;
        newsletter: WidgetContent<NewsletterWidgetData>;
    };
    visual: AssetDataBynder | AssetDataKontentAi;
};

// https://pinia.vuejs.org/core-concepts/#setup-stores
export const useBikefinderStore = defineStore('woom-bikefinder', () => {
    const bikefinder = ref<Bikefinder | null>(null);
    const bikefinderLink = ref<string | null | undefined>();
    const bikes = ref<BikeAppStore[]>([]);
    const bikesByRange = ref<BikesByRangeType[]>([]);

    function setBikes(bikeData: BikefinderQuery_Collection_Products_Items) {
        // TODO: rework with bikefinder V2
        const ranges: {
            code: string;
            sort: number;
            bikes: string[];
        }[] = [];
        bikes.value = [];
        if (bikeData) {
            const types = ['go', 'explore', 'original', 'now', ['off', 'off_air'], 'up'];
            const typesSort = [0, 1, 2, 3, 4, 5];
            for (const bike of bikeData) {
                const bikeCategory = bike?.content?.category;
                if (!bike.content || !bikeCategory) continue;
                // first get the base type of the bike
                const type = types.filter((p) => {
                    if (Array.isArray(p)) {
                        for (const t of p) {
                            if (bikeCategory.includes(t)) {
                                return true;
                            }
                        }
                    } else return bikeCategory.includes(p);
                });
                if (type && type.length === 1) {
                    const idx = types.indexOf(type[0]);
                    const sort = typesSort[idx];
                    const t = Array.isArray(type[0]) ? type[0].join('_') : type[0];
                    const exists = ranges.find((r) => r.code === t);
                    if (exists) {
                        exists.bikes.push(bike.content.id);
                    } else {
                        ranges.push({
                            code: t,
                            sort,
                            bikes: [bike.content.id],
                        });
                    }

                    bikes.value.push({
                        ...bike,
                        range: t,
                    } as BikeAppStore);
                }
            }
        }
        for (const b of ranges) {
            b.bikes = b.bikes.sort((a, b) => {
                // natural order, remove the id prefix. if the handle starts with woom-, remove that as well
                let aName = a.substring(a.indexOf('shop_product:') + 13);
                let bName = b.substring(b.indexOf('shop_product:') + 13);
                if (aName.indexOf('woom-') === 0) {
                    aName = aName.substring(5);
                }
                if (bName.indexOf('woom-') === 0) {
                    bName = bName.substring(5);
                }
                // add a sorting exception for woom-1 and woom-1-plus
                if (aName.includes('1-plus') && bName.includes('1')) return 1;
                if (aName.includes('1') && bName.includes('1-plus')) return -1;

                if (aName < bName) return -1;
                if (aName > bName) return 1;

                return 0;
            });
        }
        // now sort the range
        bikesByRange.value = ranges.sort((a, b) => {
            return a.sort - b.sort;
        });
    }

    function processBikefinderData(id: string, data: BikefinderQuery | undefined) {
        const bf = data?.bikefinder;
        if (bf) {
            // @ts-ignore TODO: better API types should fix this
            bikefinder.value = bf.bikefinder
                ? {
                      ...(bf.bikefinder as Partial<Bikefinder>),
                      flow:
                          bf.bikefinder?.flow?.map((item: WidgetList<BikefinderQuestion | BikefinderAnswer>) => {
                              if (!item) return null;
                              return item.components[0];
                          }) ?? [],
                  }
                : null;
        }

        const bikes = data?.collection?.products?.items;
        // const bikes = get(d, 'products.items', []);
        if (bikes) {
            setBikes(bikes);
        } else {
            logSentry(`no data for bikefinder with id = ${id}`, { level: 'warning' });
        }
    }

    const { queryParams } = usePreview();
    async function fetchBikefinderlinks(locale: string) {
        const fetchOptions = useGenericFetchOptions();
        const data = await $fetch('/api/bikefinder-links', {
            params: {
                locale,
                ...queryParams.value,
            },
            ...fetchOptions,
        });

        const links = data.data?.links;
        if (!links) return;
        bikefinderLink.value = getBikefinderRefLink(data.data.bikefinder?.link as LinkData | undefined, links);
    }

    return { bikefinder, bikefinderLink, bikes, fetchBikefinderlinks, processBikefinderData };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useBikefinderStore, import.meta.hot));
}
